import { Component, ErrorInfo, ReactNode, useCallback, useEffect } from 'react';

import { LinkBreak2Icon, ReloadIcon } from '@radix-ui/react-icons';
import { Button, EmptyState, Flex } from '@raystack/apsara';
import * as Sentry from '@sentry/react';

import { Show } from '@src/hoc';

import styles from './styles.module.css';

export const InternalError = ({ isRoot = false }) => {
  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  useEffect(() => {
    addEventListener('popstate', handleReload);

    return () => {
      removeEventListener('popstate', handleReload);
    };
  }, [handleReload]);

  return (
    <Flex
      align='center'
      justify='center'
      style={isRoot ? { height: '100vh', width: '100vw' } : {}}
    >
      <EmptyState
        classNames={{ container: styles.emptyState }}
        icon={<LinkBreak2Icon />}
        heading='Something Gone Wrong'
        subHeading='System is facing some issue with your request, please try again later.'
        primaryAction={
          <Button
            leadingIcon={<ReloadIcon />}
            onClick={handleReload}
            data-test-id='something-gone-wrong-btn'
          >
            Reload Now
          </Button>
        }
      />
    </Flex>
  );
};

interface Props {
  children?: ReactNode;
  isRoot?: boolean;
}

interface State {
  hasError: boolean;
  eventId?: string;
  errorInfo?: ErrorInfo;
}

const ERROR_MESSAGE = 'dynamically imported module';

function checkDynamicalImportError(message: string) {
  return !!message?.includes(ERROR_MESSAGE);
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(props: { message: string }) {
    // Update state so the next render will show the fallback UI.
    if (!checkDynamicalImportError(props.message)) {
      return { hasError: true };
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error name:', error.name);
    console.error('Error message:', error.message);
    console.error('Error stack:', error.stack);

    Sentry.withScope(scope => {
      scope.setExtras({
        ...errorInfo,
        message: error.message,
        stack: error.stack,
        name: error.name
      });
      const eventId = Sentry.captureException(error);

      if (checkDynamicalImportError(error.message)) {
        window.location.reload();
      } else {
        this.setState({ eventId, errorInfo });
      }
    });
  }

  render() {
    return (
      <Show
        when={!this.state.hasError}
        fallback={<InternalError isRoot={this.props.isRoot} />}
      >
        {this.props.children}
      </Show>
    );
  }
}
